import React, { useEffect, useState } from "react";
import { KeyRound, Copy, Ban, Trash2, Check, Database } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { StatusPill } from "@/components/LogSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const copy = (text, label) => {
  navigator.clipboard?.writeText(text);
  toast.success(`${label} copied`);
};

export default function AdminBI() {
  const [keys, setKeys] = useState([]);
  const [views, setViews] = useState([]);
  const [form, setForm] = useState({ name: "", view_ids: [] });
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState(null);

  const load = () => api.get("/admin/bi-keys").then(({ data }) => setKeys(data));

  useEffect(() => {
    load();
    api.get("/control-views").then(({ data }) => setViews(data.filter((v) => v.bi_enabled)));
  }, []);

  const base = `${process.env.REACT_APP_BACKEND_URL}/api/bi/v1`;

  const createKey = async () => {
    if (!form.name.trim()) return toast.error("Give the key a name, e.g. 'Power BI — PMO dashboard'");
    try {
      const { data } = await api.post("/admin/bi-keys", { name: form.name.trim(), view_ids: form.view_ids });
      setCreated(data);
      setOpen(false);
      setForm({ name: "", view_ids: [] });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Any BI report using it will stop refreshing.`)) return;
    try {
      await api.post(`/admin/bi-keys/${k.id}/revoke`);
      load();
      toast.success("Key revoked");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (k) => {
    if (!window.confirm(`Delete the record for "${k.name}"?`)) return;
    try {
      await api.delete(`/admin/bi-keys/${k.id}`);
      load();
      toast.success("Deleted");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <Shell
      testId="admin-bi-page"
      title="BI Feeds"
      subtitle="Connect Power BI, Tableau or Excel straight to live Videra PM data — read only, no exports to manage"
      actions={
        <Button data-testid="new-bi-key-btn" onClick={() => setOpen(true)} className="gap-2" style={{ backgroundColor: "var(--brand-primary)" }}>
          <KeyRound size={15} /> New Access Key
        </Button>
      }
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <section data-testid="bi-keys-panel">
            <h3 className="mb-2 font-display text-base font-semibold text-slate-800">Access Keys</h3>
            {keys.length === 0 ? (
              <Empty testId="bi-keys-empty" text="No BI keys yet. Create one, then paste it into your BI tool." />
            ) : (
              <div className="panel overflow-x-auto">
                <table className="w-full min-w-[760px] text-sm">
                  <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                    <tr>{["Name", "Key", "Scope", "Last Used", "Calls", "Status", ""].map((h) => (
                      <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {keys.map((k) => (
                      <tr key={k.id} data-testid={`bi-key-row-${k.id}`}>
                        <td className="px-4 py-3 font-medium text-slate-800">{k.name}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">{k.display_prefix}…</td>
                        <td className="px-4 py-3 text-slate-600">
                          {k.view_ids?.length ? `${k.view_ids.length} saved view${k.view_ids.length === 1 ? "" : "s"}` : "All datasets"}
                        </td>
                        <td className="px-4 py-3 text-slate-500">{k.last_used_at?.slice(0, 10) || "Never"}</td>
                        <td className="px-4 py-3 text-slate-500">{k.call_count || 0}</td>
                        <td className="px-4 py-3"><StatusPill value={k.revoked_at ? "Revoked" : "Active"} /></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {!k.revoked_at && (
                              <button data-testid={`revoke-bi-key-${k.id}`} onClick={() => revoke(k)} aria-label="Revoke key" title="Revoke key" className="text-slate-400 hover:text-amber-600">
                                <Ban size={15} />
                              </button>
                            )}
                            <button data-testid={`delete-bi-key-${k.id}`} onClick={() => remove(k)} aria-label="Delete key" title="Delete key" className="text-slate-400 hover:text-red-600">
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="panel p-5" data-testid="bi-feed-urls-panel">
            <h3 className="font-display text-base font-semibold text-slate-800">Feed URLs</h3>
            <p className="mt-1 text-xs text-slate-500">
              Start with the catalogue &mdash; it lists every feed your key can reach. Add your key as an <code>X-API-Key</code> header,
              or append <code>?key=&hellip;</code> if your tool cannot set headers. Add <code>?format=csv</code> for a CSV feed.
            </p>
            <div className="mt-4 space-y-2">
              {[
                { label: "Catalogue (start here)", url: `${base}/datasets` },
                { label: "All projects", url: `${base}/datasets/projects` },
                { label: "All risks", url: `${base}/datasets/risks` },
                { label: "All milestones", url: `${base}/datasets/milestones` },
                { label: "All status reports", url: `${base}/datasets/status-reports` },
                ...views.map((v) => ({ label: `Saved view — ${v.name}`, url: `${base}/views/${v.id}` })),
              ].map((f) => (
                <div key={f.url} className="flex items-center justify-between gap-3 rounded border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-slate-700">{f.label}</div>
                    <div className="truncate font-mono text-[11px] text-slate-500">{f.url}</div>
                  </div>
                  <button data-testid={`copy-feed-${f.label.replace(/\W+/g, "-").toLowerCase()}`} onClick={() => copy(f.url, "Feed URL")} aria-label="Copy feed URL" title="Copy feed URL" className="shrink-0 text-slate-400 hover:text-slate-800">
                    <Copy size={15} />
                  </button>
                </div>
              ))}
            </div>
            {views.length === 0 && (
              <p className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                No saved views are published to BI yet. Open any register, save a view and tick &ldquo;Publish to BI&rdquo;.
              </p>
            )}
          </section>
        </div>

        <section className="panel p-5" data-testid="bi-howto-panel">
          <h3 className="font-display text-base font-semibold text-slate-800">How to connect</h3>
          <div className="mt-3 space-y-4 text-sm text-slate-600">
            <div>
              <div className="font-semibold text-slate-800">Power BI</div>
              <p className="mt-1 leading-relaxed">Get Data → Web → Advanced. Paste a feed URL, add header <code>X-API-Key</code> with your key, choose Anonymous. Set a scheduled refresh and it stays live.</p>
            </div>
            <div>
              <div className="font-semibold text-slate-800">Excel / Power Query</div>
              <p className="mt-1 leading-relaxed">Data → Get Data → From Other Sources → From Web, then the same URL and header.</p>
            </div>
            <div>
              <div className="font-semibold text-slate-800">Tableau</div>
              <p className="mt-1 leading-relaxed">Use the CSV feed (<code>?format=csv</code>) with a Web/Text connector, or a Web Data Connector with the header set server-side.</p>
            </div>
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs">
              <div className="mb-1 flex items-center gap-1.5 font-semibold text-slate-700"><Database size={13} /> Good practice</div>
              One key per report or per person, so you can revoke a single key without breaking everything else. Keys are read-only and can never change your data.
            </div>
          </div>
        </section>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="bi-key-dialog" className="max-h-[80vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New BI Access Key</DialogTitle>
            <DialogDescription>Read-only. You&rsquo;ll see the key once &mdash; copy it straight into your BI tool.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="field-label">Key Name</label>
              <Input data-testid="bi-key-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Power BI — PMO dashboard" />
            </div>
            <div>
              <label className="field-label">Limit to specific saved views (optional)</label>
              <p className="mb-2 text-xs text-slate-500">Leave all unticked to allow every dataset and published view.</p>
              {views.length === 0 ? (
                <p className="text-xs text-slate-400">No views published to BI yet.</p>
              ) : (
                <div className="space-y-1.5">
                  {views.map((v) => (
                    <label key={v.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        data-testid={`bi-key-view-${v.id}`}
                        type="checkbox"
                        className="h-4 w-4"
                        checked={form.view_ids.includes(v.id)}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            view_ids: e.target.checked ? [...f.view_ids, v.id] : f.view_ids.filter((x) => x !== v.id),
                          }))
                        }
                      />
                      {v.name} <span className="text-xs text-slate-400">({v.kind})</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="bi-key-cancel-btn" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="bi-key-create-btn" onClick={createKey} style={{ backgroundColor: "var(--brand-primary)" }}>Create Key</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!created} onOpenChange={(o) => !o && setCreated(null)}>
        <DialogContent data-testid="bi-key-created-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Copy your key now</DialogTitle>
            <DialogDescription>This is the only time it will be shown. If you lose it, revoke it and create another.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 p-3">
            <code data-testid="bi-key-value" className="min-w-0 flex-1 break-all font-mono text-xs text-slate-800">{created?.key}</code>
            <Button size="sm" variant="outline" data-testid="copy-bi-key-btn" onClick={() => copy(created.key, "Key")} className="shrink-0 gap-1.5">
              <Copy size={14} /> Copy
            </Button>
          </div>
          <DialogFooter>
            <Button data-testid="bi-key-done-btn" onClick={() => setCreated(null)} className="gap-1.5" style={{ backgroundColor: "var(--brand-primary)" }}>
              <Check size={14} /> I&rsquo;ve saved it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
